import { Download } from 'react-feather';
import { startOfDay } from 'date-fns';
import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';
import range from 'lodash/range';

import { Breadcrumbs, EventHeader, NavItem, Section } from '../../Common/Layout';
import { EventHeader as EventHeaderImage } from '../../../common/Common/EventHeader';
import { RegistrationSummary } from '../helpers';
import { assetUrl } from '../../../common/helpers';
import { useBrandColor } from '../../../common/Common/ThemeProvider';
import { useGuard } from '../Guard';
import { useNavigation } from '../../Common/useNavigation';
import { useParticipant } from '../ParticipantProvider';
import { useRegistrationsUrlState } from './helpers';
import GetRegistrationSummaryForDashboardQuery from './GetRegistrationSummaryForDashboardQuery';
import GetSupportaCampaignsQuery from './GetSupportaCampaignsQuery';
import MessagesSection from './MessagesSection';
import RegistrationsSection from './RegistrationsSection';
import SupportaSection from './SupportaSection';
import TeamsSection from './TeamsSection';
import UI from '../../../common/UI';
import UnassignedRegistrationsSection from './UnassignedRegistrationsSection';
import WaitlistRegistrationsSection from './WaitlistRegistrationsSection';
import useDocumentTitle from '../../../common/useDocumentTitle';
import useLocale from '../../../common/useLocale';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';

export interface RegistrationsPageProps {
  participantId: string;
  eventId: string;
  token: string;
}

const RegistrationsPage = ({
  participantId, eventId, token,
}: RegistrationsPageProps) => {
  const { t } = useTranslation();
  const { r } = useNavigation();
  const history = useHistory();
  const { participant } = useParticipant();

  const [urlState] = useRegistrationsUrlState();

  const { data, refetch } = useQuery(
    GetRegistrationSummaryForDashboardQuery,
    {
      variables: {
        participant: participantId,
        event: eventId,
        token,
        registrations_limit: urlState.registrations.limit || 10,
        registration_filters: {
          search: urlState.registrations.search,
          tickets: urlState.registrations.ticket ? [urlState.registrations.ticket] : null,
          teams: urlState.registrations.team && urlState.registrations.team !== 'no_team'
            ? [urlState.registrations.team] : null,
          has_team: urlState.registrations.team === 'no_team' ? false : null,
          assignment_statuses: urlState.registrations.assignment_statuses,
        },
        waitlist_registrations_limit: urlState.waitlist_registrations.limit || 10,
        teams_ids: urlState.teams.id ? [urlState.teams.id] : undefined,
        teams_search: urlState.teams.search,
        teams_limit: urlState.teams.limit,
        include_filtered_teams: !!urlState.teams.search,
      },
      onError: (error) => {
        if (error.graphQLErrors?.length > 0) {
          // Redirect only in case of a normal authentication/validation error.
          history.replace(r('Portal'));
        }
      },
    },
  );

  const summary = data?.registrationSummary;

  useDocumentTitle([
    summary?.participant.full_name,
    summary?.event.title,
    'Atleta',
  ]);

  const themeInitialized = useBrandColor(summary?.event.brand_color);

  const participantRef = useRef(participant);

  // Refetch summary after logging in
  useEffect(() => {
    if (data && participant !== participantRef.current) {
      refetch();
      participantRef.current = participant;
    }
  }, [data, participant, refetch]);

  const order = summary?.orders.filter((order) => order.id === urlState.orderId)[0];

  const showSupportaSection = summary?.event.enable_fundraising;

  const { data: supportaData } = useQuery(
    GetSupportaCampaignsQuery,
    {
      variables: {
        participant: participantId,
        event: eventId,
        token,
      },
      skip: !showSupportaSection,
    },
  );

  if (!data || !themeInitialized) {
    return <UI.PageLoader />;
  }

  const hasUnassignedRegistrations = summary.assigned_ticket_counts.some(
    (group) => group.assignable_count > 0,
  );

  const nothingToSee = summary.registrations_count
    + summary.waitlist_registrations_count
    + summary.teams.length
    + summary.sent_messages.length === 0
    && !showSupportaSection;

  return (
    <>
      <Header summary={summary} />

      {urlState.orderId && (
        <UI.Div sc={{ px: [2, 4] }}>
          <UI.Success sc={{ relative: true }} style={{ overflow: 'hidden' }}>
            <img
              src={assetUrl('images/confetti.svg')}
              style={{
                position: 'absolute',
                inset: 0,
                display: 'block',
                objectFit: 'cover',
                objectPosition: 'center center3',
                zIndex: 1,
                opacity: 0.2,
              }}
              alt={t('order_confirmation_title')}
            />
            <UI.Div style={{ position: 'relative', zIndex: 2 }}>
              <UI.H5>
                {t('order_confirmation_title')}
              </UI.H5>
              {order && t('order_confirmation_description', { number: order.invoice_id })}
            </UI.Div>
          </UI.Success>
        </UI.Div>
      )}

      {summary.registrations_count > 0 && (
        <>
          {hasUnassignedRegistrations && summary.registrations_count > 1 && (
          <UnassignedRegistrationsSection summary={summary} />
          )}

          <RegistrationsSection summary={summary} />
        </>
      )}

      {summary.waitlist_registrations_count > 0 && (
        <WaitlistRegistrationsSection summary={summary} />
      )}

      {summary.teams.length > 0 && (
        <TeamsSection summary={summary} />
      )}

      {summary.sent_messages.length > 0 && (
        <MessagesSection messages={summary.sent_messages} />
      )}

      {showSupportaSection && (
        <SupportaSection
          summary={summary}
          campaigns={supportaData?.registrationSummary.supporta_campaigns}
        />
      )}

      {nothingToSee && (
        <Section>
          <UI.Div sc={{ muted: true }}>
            {t('no_registrations_yet')}
          </UI.Div>
        </Section>
      )}
    </>
  );
};

interface HeaderProps {
  summary: RegistrationSummary;
}

const Header = ({ summary }: HeaderProps) => {
  const { t } = useTranslation();
  const { r } = useNavigation();
  const { formatDate, parseDate, formatNumber } = useLocale();
  const { guard } = useGuard(summary.participant);
  const { impersonating } = useParticipant();
  const project = useProject();

  const { event, participant } = summary;

  const ticketsCount = summary.registrations_count;

  const showUpgradesOption = summary.has_upgrades_available;
  const showOrdersOption = summary.orders_count > 0;
  const showExportParticipantsOption = ticketsCount > 1 && summary.is_company;
  const showDownloadTicketsOption = event.enable_tickets && ticketsCount > 0;

  const showOptions = showUpgradesOption
    || showOrdersOption
    || showExportParticipantsOption
    || showDownloadTicketsOption;

  const [openExportModal, closeExportModal] = useModal(() => (
    <ExportParticipantsModal url={summary.export_participants_url} onClose={closeExportModal} />
  ), [summary.export_participants_url]);

  const [openDownloadTicketsModal, closeDownloadTicketsModal] = useModal(() => (
    <DownloadTicketsModal url={summary.tickets_url} ticketsCount={ticketsCount} onClose={closeDownloadTicketsModal} />
  ), [summary.tickets_url, ticketsCount]);

  const ticketsAvailableFrom = event.tickets_available_from
    ? parseDate(summary.event.tickets_available_from, { timezone: 'UTC' })
    : null;

  return (
    <>
      <Breadcrumbs hideHome={impersonating}>
        <UI.Strong>{event.title}</UI.Strong>
      </Breadcrumbs>

      {event.header && (
        <EventHeaderImage
          headerUrl={event.header.url}
          sportType={event.ticket_categories[0]?.sport_type}
        />
      )}

      <EventHeader event={event} />

      {showOptions && (
        <Section secondary>
          {showUpgradesOption && (
            <NavItem
              to={r('Upgrades', {
                projectId: project.id,
                participantId: participant.id,
                eventId: event.id,
                token: summary.view_token,
              })}
              title={t('order_extras')}
              alignVertical="center"
              sc={{ py: 2 }}
            >
              <UI.Span sc={{ fontWeight: 500 }}>
                {t('order_extras')}
              </UI.Span>
            </NavItem>
          )}
          {showOrdersOption && (
            <NavItem
              to={r('Orders', {
                projectId: project.id,
                participantId: participant.id,
                eventId: event.id,
                token: summary.view_token,
              })}
              title={t('orders')}
              alignVertical="center"
              sc={{ py: 2 }}
            >
              <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <UI.Span sc={{ fontWeight: 500 }}>
                  {t('orders')}
                </UI.Span>
                <UI.Badge
                  sc={{
                    background: 'gray.250', outline: false, color: 'gray.500', ml: 2,
                  }}
                  style={{ marginTop: -1, marginBottom: -1 }}
                >
                  {formatNumber(summary.orders_count)}
                </UI.Badge>
              </UI.FlexContainer>
            </NavItem>
          )}
          {showExportParticipantsOption && (
            <NavItem
              onClick={() => guard(openExportModal)}
              title={t('export_participants')}
              icon={<Download />}
              alignVertical="center"
              sc={{ py: 2 }}
            >
              <UI.Span sc={{ fontWeight: 500 }} style={{ minWidth: 0 }}>
                <UI.Truncate>
                  {t('export_participants')}
                </UI.Truncate>
              </UI.Span>
            </NavItem>
          )}
          {showDownloadTicketsOption && (
            <NavItem
              href={event.tickets_available && ticketsCount <= 100 ? summary.tickets_url : undefined}
              onClick={event.tickets_available && ticketsCount > 100 ? () => openDownloadTicketsModal() : undefined}
              target="_blank"
              rel="noopener noreferrer"
              title={ticketsCount > 1 ? t('download_tickets') : t('download_ticket')}
              icon={<Download />}
              alignVertical="center"
              sc={{ py: 2 }}
            >
              <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <UI.Span sc={{ fontWeight: 500, muted: !event.tickets_available }} style={{ minWidth: 0 }}>
                  <UI.Truncate>
                    {ticketsCount > 1 ? t('download_tickets') : t('download_ticket')}
                  </UI.Truncate>
                </UI.Span>
                <UI.Span sc={{ flex: {} }}>
                  {!event.tickets_available && ticketsAvailableFrom && (
                    <UI.Span sc={{ ml: 1 }}>
                      <UI.Label
                        sc={{ background: 'gray.250', color: 'gray.600', noWrap: true }}
                        style={{ fontSize: '0.75em' }}
                      >
                        {t('from_date', { date: formatDate(ticketsAvailableFrom, {
                          format: startOfDay(ticketsAvailableFrom).getTime() === ticketsAvailableFrom.getTime()
                            ? 'display_date_without_year'
                            : 'display_date_time_without_year',
                        }) })}
                      </UI.Label>
                    </UI.Span>
                  )}
                  {ticketsCount > 1 && (
                    <UI.Badge
                      sc={{
                        background: 'gray.250', outline: false, color: 'gray.500', ml: 2,
                      }}
                      style={{ marginTop: -1, marginBottom: -1 }}
                    >
                      {formatNumber(ticketsCount)}
                    </UI.Badge>
                  )}
                </UI.Span>

              </UI.FlexContainer>
            </NavItem>
          )}
        </Section>
      )}
      <UI.HR />
    </>
  );
};

const DownloadTicketsModal = ({ url, ticketsCount, onClose }: {
  url: string; ticketsCount: number, onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const batches = range(1, Math.ceil(ticketsCount / 100) + 1);

  return (
    <UI.Modal close={onClose}>
      <UI.GridContainer>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('download_tickets')}
          </UI.H4>
          {t('download_tickets_description')}
        </UI.Div>

        <UI.GridContainer sc={{ gutter: 0.5 }}>
          {batches.map((batch) => (
            <UI.AButton
              href={`${url}?batch=${batch}`}
              target="_blank"
              rel="noopener noreferrer"
              key={batch}
            >
              <UI.Icon>
                <Download />
              </UI.Icon>
              {' '}
              {t('tickets_x_to_y', {
                x: formatNumber((batch - 1) * 100 + 1),
                y: formatNumber(Math.min(batch * 100, ticketsCount)),
              })}
            </UI.AButton>
          ))}
        </UI.GridContainer>
      </UI.GridContainer>
    </UI.Modal>
  );
};

const ExportParticipantsModal = ({ url, onClose }: { url: string; onClose: () => void; }) => {
  const { t } = useTranslation();

  return (
    <UI.Modal close={onClose}>
      <UI.GridContainer>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('export_participants')}
          </UI.H4>
          {t('export_participants_description')}
        </UI.Div>

        <UI.GridContainer sc={{ columns: '1fr 1fr', gutter: 0.5 }}>
          <UI.AButton href={`${url}?format=csv`} onClick={onClose}>
            <UI.Icon>
              <Download />
            </UI.Icon>
            {' '}
            {t('csv')}
          </UI.AButton>
          <UI.AButton href={`${url}?format=excel`} onClick={onClose}>
            <UI.Icon>
              <Download />
            </UI.Icon>
            {' '}
            {t('excel')}
          </UI.AButton>
        </UI.GridContainer>
      </UI.GridContainer>
    </UI.Modal>
  );
};

export default RegistrationsPage;
